import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../store';
import type { Folder, Rubric, Reference } from '../domain/Roaddata';
import type { RubricId } from '../domain/Session';
import { WIDTH_PARTIAL_CODE } from '..';

const slice = (state: RootState) => state.catalogs;

export const catalogsQuery = createSelector(slice, (state) => state.catalogs);
export const allReferencesQuery = (state): Reference[] => {
    const rubrics = allRubricsQuery(state);
    const seenKeys = new Set<string>();

    const distinctReferences: Set<Reference> = rubrics.reduce((references, rubric) => {
        rubric.references.forEach((ref) => {
            if (!seenKeys.has(ref.key)) {
                references.add(ref);
                seenKeys.add(ref.key);
            }
        });

        return references;
    }, new Set<Reference>());

    return Array.from(distinctReferences);
};

export const gradedAndWidthQuery = (state): Reference[] => {
    const rubrics = allRubricsQuery(state);
    const seenKeys = new Set<string>();

    const distinctReferences: Set<Reference> = rubrics.reduce((references, rubric) => {
        rubric.references.forEach((ref) => {
            if ((ref.isGraded || ref.key.includes(WIDTH_PARTIAL_CODE)) && !seenKeys.has(ref.key)) {
                references.add(ref);
                seenKeys.add(ref.key);
            }
        });

        return references;
    }, new Set<Reference>());

    return Array.from(distinctReferences);
};

export const allRubricsQuery = createSelector(slice, (state): Rubric[] => {
    const foundRubrics: Array<Rubric> = [];

    const exploreFolders = (foldersToExplore: Array<Folder>) => {
        foldersToExplore.forEach(({ rubrics, folders }) => {
            if (rubrics.length > 0) {
                rubrics.forEach((rubric) => {
                    foundRubrics.push(rubric);
                });
            }
            if (folders.length > 0) {
                exploreFolders(folders);
            }
        });
    };

    exploreFolders(state.catalogs);

    return foundRubrics;
});

export const rubricByIdQuery = (rubricId: RubricId) => (state): Rubric | undefined => {
    const allRubrics = allRubricsQuery(state);

    return allRubrics.find((rubric) => rubric.id === rubricId);
};

export const referencesByRubricIdQuery = (rubricId: RubricId) => (state): Reference[] => {
    const rubric = rubricByIdQuery(rubricId)(state);

    return rubric?.references ?? [];
};

export const sortedReferencesByRubricIdQuery = (rubricId: RubricId | undefined) =>
    createSelector(
        (state): Reference[] => (rubricId ? referencesByRubricIdQuery(rubricId)(state) : []),
        (references): Reference[] => [...references].sort((a, b) => a.key.localeCompare(b.key)),
    );

export const allReferenceIdsQuery = (state): Array<string> => {
    const rubrics = allRubricsQuery(state);

    return rubrics
        .map((rubric) => rubric.references)
        .flat()
        .map((r) => r.id);
};

export const allRubricsIdsQuery = (state): Array<string> => {
    const rubrics = allRubricsQuery(state);

    return rubrics.map((rubric) => rubric.id);
};

export type GroupedReferences = {
    parent: Reference;
    children: Reference[];
};

export type GradeFilterRubricItem = {
    rubric: Rubric;
    groups: GroupedReferences[];
    flatRefs: Reference[];
};

// Display order for the "État global de mon réseau" filter panel: Dégradation
// chaussée always first, Largeur et surface always second, then everything
// else in its existing relative order, with Signalisation Verticale always
// last. Rubrics not listed here keep their relative order in between.
const RUBRIC_FILTER_PANEL_ORDER: Record<string, number> = {
    DEGRADATION_CHAUSSEE: 0,
    LARGEUR_ET_SURFACE: 1,
    SV: Number.MAX_SAFE_INTEGER,
};

const rubricFilterPanelRank = (rubric: Rubric): number =>
    rubric.code && rubric.code in RUBRIC_FILTER_PANEL_ORDER ? RUBRIC_FILTER_PANEL_ORDER[rubric.code] : 2;

export const gradedRubricsForFilterPanelQuery = (state): GradeFilterRubricItem[] => {
    const rubrics = allRubricsQuery(state);
    const result: GradeFilterRubricItem[] = [];

    for (const rubric of rubrics) {
        const gradedRefs = rubric.references.filter(
            (r) => r.isGraded || r.key.includes(WIDTH_PARTIAL_CODE),
        );
        if (gradedRefs.length === 0) continue;

        const hasHierarchy = gradedRefs.some((r) => r.parentId);

        if (hasHierarchy) {
            const childrenByParentId = new Map<string, Reference[]>();
            gradedRefs.forEach((r) => {
                if (r.parentId) {
                    const existing = childrenByParentId.get(r.parentId) || [];
                    existing.push(r);
                    childrenByParentId.set(r.parentId, existing);
                }
            });

            const groups = gradedRefs
                .filter((r) => !r.parentId && childrenByParentId.has(r.id))
                .sort((a, b) => a.key.localeCompare(b.key))
                .map((parent) => ({
                    parent,
                    children: (childrenByParentId.get(parent.id) || []).sort((a, b) =>
                        a.key.localeCompare(b.key),
                    ),
                }));

            // Standalone refs: no parentId and not a parent of any child
            const standaloneRefs = gradedRefs
                .filter((r) => !r.parentId && !childrenByParentId.has(r.id))
                .sort((a, b) => a.name.localeCompare(b.name));

            result.push({ rubric, groups, flatRefs: standaloneRefs });
        } else {
            const flatRefs = [...gradedRefs].sort((a, b) => a.name.localeCompare(b.name));
            result.push({ rubric, groups: [], flatRefs });
        }
    }

    return result.sort((a, b) => rubricFilterPanelRank(a.rubric) - rubricFilterPanelRank(b.rubric));
};

export const groupedReferencesByRubricIdQuery = (rubricId: RubricId | undefined) =>
    createSelector(
        (state): Reference[] => (rubricId ? referencesByRubricIdQuery(rubricId)(state) : []),
        (references): GroupedReferences[] => {
            const parents = references
                .filter((r) => !r.parentId)
                .sort((a, b) => a.key.localeCompare(b.key));

            const groups = parents.map((parent) => ({
                parent,
                children: references
                    .filter((r) => r.parentId === parent.id)
                    .sort((a, b) => a.key.localeCompare(b.key)),
            }));

            // Groups with children (real parents) always sort above standalone
            // references (no parent, no children — e.g. "Autre", "Nid de
            // poule"), which trail at the bottom instead of interleaving
            // alphabetically with the real parent groups.
            return [
                ...groups.filter((g) => g.children.length > 0),
                ...groups.filter((g) => g.children.length === 0),
            ];
        },
    );

export const selectedReferenceByRubricIdQuery = (rubricId: RubricId) =>
    createSelector(
        slice,
        (state): Reference | undefined =>
            state.selectedReferenceByRubricIds.find((refByRubricId) => refByRubricId.rubricId === rubricId)?.reference,
    );
